import UserCustomSignatureParams from "@/domain/entities/user/UserCustomSignature";
import { queryKeys } from "@/lib/queryKeys";
import sanitizeHTML from "@/lib/sanitizeHTML";
import { userSignatureToDomainMapper } from "@/mappers/user/userSignatureMapper";
import { useSdk } from "@cs_teamwaydev/mailvista-sdk_react";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import useFetchErrorNotifier from "../utils/useFetchErrorNotifier";
import getSignatureTemplate from "./getTemplate";

const useGetUserSignature = (): {
    userSanitizedSignature: UseQueryResult<string | null, Error>;
    userSignatureParams: UseQueryResult<
        UserCustomSignatureParams | null,
        Error
    >;
} => {
    const { userManager } = useSdk();
    const notifyFetchError = useFetchErrorNotifier();

    const getUserSignatureParams =
        async (): Promise<UserCustomSignatureParams | null> => {
            try {
                const result = await userManager.getUserSignatures(); // TODO: the SDK will use another method to get the signature once template is implemented

                if (result.length > 0) {
                    return userSignatureToDomainMapper(result[0]);
                }

                return null;
            } catch (error) {
                console.error(error);
                notifyFetchError({
                    queryKey: queryKeys.user.signature(),
                    titleTranslationKey: "errors.signature.get",
                });

                // Returning null here would cache a failure as a success.
                throw error;
            }
        };

    const userSignatureParams = useQuery({
        queryKey: queryKeys.user.signature(),
        queryFn: getUserSignatureParams,
    });

    // Same queryKey as above: one cache entry, so one network call for both.
    const userSanitizedSignature = useQuery({
        queryKey: queryKeys.user.signature(),
        queryFn: getUserSignatureParams,
        select: (params) =>
            params ? sanitizeHTML(getSignatureTemplate(params)) : null,
    });

    return {
        userSanitizedSignature,
        userSignatureParams,
    };
};

export default useGetUserSignature;
